/**
 * useConsentStore.js
 * Quản lý trạng thái cookie consent của người dùng.
 * - status: "unknown" | "accepted" | "declined"
 * - Hydrate từ localStorage khi app khởi động
 * - Quyết định storage backend cho guest cart/wishlist
 */
import { create } from "zustand";

const CONSENT_STORAGE_KEY = "lianne_cookie_consent";
const GUEST_STORAGE_KEYS = [
  "lianne_guest_cart",
  "lianne_guest_wishlist",
  "lianne_guest_id",
];

const readJSON = (storage, key, fallback) => {
  try {
    const raw = storage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const writeJSON = (storage, key, value) => {
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch {}
};

const mergeCartItems = (persistentItems, sessionItems) => {
  const merged = [...(persistentItems || [])];

  (sessionItems || []).forEach((sessionItem) => {
    const key =
      sessionItem.local_key ||
      sessionItem.cart_item_key ||
      `${sessionItem.product_id || sessionItem.id}:${sessionItem.variation_id || 0}`;
    const existingIndex = merged.findIndex((item) => {
      const itemKey =
        item.local_key ||
        item.cart_item_key ||
        `${item.product_id || item.id}:${item.variation_id || 0}`;
      return itemKey === key;
    });

    if (existingIndex >= 0) {
      merged[existingIndex] = {
        ...merged[existingIndex],
        ...sessionItem,
        quantity:
          Number(merged[existingIndex].quantity || 0) +
          Number(sessionItem.quantity || 0),
      };
    } else {
      merged.push(sessionItem);
    }
  });

  return merged;
};

const mergeWishlistItems = (persistentValue, sessionValue) => {
  const toArray = (value) => {
    if (Array.isArray(value)) return value;
    if (Array.isArray(value?.product_ids)) {
      return value.product_ids.map((id) => ({
        id,
        ...(value.items?.[id] || value.items?.[String(id)] || {}),
      }));
    }
    return [];
  };

  const itemMap = new Map();
  [...toArray(persistentValue), ...toArray(sessionValue)].forEach((item) => {
    const productId = Number(item.id || item.product_id);
    if (productId) {
      itemMap.set(productId, {
        ...item,
        id: productId,
        product_id: productId,
      });
    }
  });

  return Array.from(itemMap.values());
};

const migrateGuestDataToLocalStorage = () => {
  GUEST_STORAGE_KEYS.forEach((key) => {
    try {
      const sessionValue = sessionStorage.getItem(key);
      if (!sessionValue) return;

      if (key === "lianne_guest_cart") {
        const merged = mergeCartItems(
          readJSON(localStorage, key, []),
          readJSON(sessionStorage, key, [])
        );
        writeJSON(localStorage, key, merged);
      } else if (key === "lianne_guest_wishlist") {
        const merged = mergeWishlistItems(
          readJSON(localStorage, key, []),
          readJSON(sessionStorage, key, [])
        );
        writeJSON(localStorage, key, merged);
      } else if (!localStorage.getItem(key)) {
        localStorage.setItem(key, sessionValue);
      }

      sessionStorage.removeItem(key);
    } catch {}
  });
};

const removePersistentGuestData = () => {
  GUEST_STORAGE_KEYS.forEach((key) => {
    try {
      localStorage.removeItem(key);
    } catch {}
  });
};

const migrateGuestDataToSessionStorage = () => {
  GUEST_STORAGE_KEYS.forEach((key) => {
    try {
      const localValue = localStorage.getItem(key);
      if (!localValue) return;

      if (key === "lianne_guest_cart") {
        const merged = mergeCartItems(
          readJSON(sessionStorage, key, []),
          readJSON(localStorage, key, [])
        );
        writeJSON(sessionStorage, key, merged);
      } else if (key === "lianne_guest_wishlist") {
        const merged = mergeWishlistItems(
          readJSON(sessionStorage, key, []),
          readJSON(localStorage, key, [])
        );
        writeJSON(sessionStorage, key, merged);
      } else if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, localValue);
      }
    } catch {}
  });
};

const useConsentStore = create((set, get) => ({
  // ─── State ────────────────────────────────────────────────────────────────
  status: "unknown", // "unknown" | "accepted" | "declined"
  isHydrated: false,

  // ─── Hydrate ──────────────────────────────────────────────────────────────
  /**
   * Gọi 1 lần khi app init (DefaultLayout).
   * Đọc localStorage để khôi phục quyết định consent cũ.
   */
  hydrate: () => {
    try {
      const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
      if (stored === "accepted" || stored === "declined") {
        set({ status: stored, isHydrated: true });
      } else {
        set({ status: "unknown", isHydrated: true });
      }
    } catch {
      // localStorage không khả dụng (private mode cực đoan)
      set({ status: "unknown", isHydrated: true });
    }
  },

  // ─── Actions ──────────────────────────────────────────────────────────────
  accept: () => {
    try {
      localStorage.setItem(CONSENT_STORAGE_KEY, "accepted");
      migrateGuestDataToLocalStorage();
    } catch {}
    set({ status: "accepted" });
  },

  decline: () => {
    try {
      migrateGuestDataToSessionStorage();
      localStorage.setItem(CONSENT_STORAGE_KEY, "declined");
      removePersistentGuestData();
    } catch {}
    set({ status: "declined" });
  },

  clearConsent: () => {
    try {
      localStorage.removeItem(CONSENT_STORAGE_KEY);
    } catch {}
    set({ status: "unknown" });
  },

  // ─── Helpers ──────────────────────────────────────────────────────────────
  /**
   * Trả về storage phù hợp với consent hiện tại.
   * - accepted  → localStorage (persist qua session)
   * - declined  → sessionStorage (mất khi đóng tab, expected)
   * - unknown   → sessionStorage (fallback an toàn)
   */
  getStorage: () => {
    const { status } = get();
    if (status === "accepted") {
      return localStorage;
    }
    return sessionStorage;
  },

  /**
   * Trả về Promise resolve khi consent đã hydrate xong.
   * Dùng cho các action cần chờ hydrate trước khi đọc storage.
   */
  waitForHydration: () => {
    return new Promise((resolve) => {
      const check = () => {
        if (get().isHydrated) {
          resolve();
        } else {
          setTimeout(check, 20);
        }
      };
      check();
    });
  },
}));

export default useConsentStore;
